import { ScrollText, History } from "lucide-react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { TermsBody, useTosVersion } from "@/components/TermsBody";
import { useReleaseDateLabel } from "@/hooks/useReleaseMetadata";
import { ReleaseDate } from "@/components/ReleaseDate";
import { PatchNotesButton } from "@/components/PatchNotesButton";
import { LegalCompanyInfo } from "@/components/LegalCompanyInfo";
import { api } from "@/lib/api";

export default function TermsPage() {
  const version = useTosVersion();
  const releaseDate = useReleaseDateLabel();
  const { data: history } = useQuery({
    queryKey: ["tos-history", version],
    queryFn: async () => (await api.get("/tos/history")).data,
    staleTime: 5 * 60 * 1000,
  });
  const versions = history?.versions || [];
  const revisionCount = history?.total ?? versions.length;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12" data-testid="terms-page">
      <div className="hud-pill rounded-2xl p-6 sm:p-10">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-11 w-11 rounded-xl bg-cyan-400/15 border border-cyan-300/30 flex items-center justify-center shrink-0">
            <ScrollText className="h-5 w-5 text-cyan-300" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-cyan-300/80">Legal · Binding Agreement</div>
            <h1 className="font-display text-3xl sm:text-4xl text-white tracking-[0.05em]">TERMS OF SERVICE</h1>
          </div>
        </div>
        <p data-testid="terms-current-version" className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40 mb-2">
          Playden Inc. · Version {version} (&quot;Playden V {version}&quot;) · {releaseDate} ·{" "}
          <Link to="/legal/privacy" data-testid="terms-privacy-link" className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2">Privacy Policy</Link>
        </p>
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40 mb-4" data-testid="terms-revision-counter">
          <span className="text-cyan-300">{revisionCount}</span> recorded releases
        </p>
        <div className="mb-8"><PatchNotesButton testId="terms-view-changelog" /></div>
        <TermsBody />

        {versions.length > 0 && (
          <div className="mt-10 pt-6 border-t border-white/10" data-testid="terms-version-history">
            <div className="flex items-center gap-2 mb-3">
              <History className="h-4 w-4 text-cyan-300" />
              <span data-testid="terms-history-title" className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-300">Release history · {revisionCount} updates</span>
            </div>
            <div className="rounded-2xl border border-white/10 bg-black/30 overflow-hidden">
              {[...versions].reverse().map((v, i) => (
                <div
                  key={v.version}
                  data-testid={`terms-history-row-${v.version}`}
                  className={`px-4 py-3 border-t border-white/5 ${v.version === version ? "bg-cyan-400/5" : ""} first:border-t-0`}
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-3">
                    <div className="font-mono text-xs text-white">
                      V <span className="text-cyan-300">{v.version}</span>
                      {v.version === version && (
                        <span className="ml-2 font-mono text-[9px] uppercase tracking-[0.24em] text-cyan-300/90 border border-cyan-300/40 bg-cyan-400/10 rounded-full px-1.5 py-0.5">Current</span>
                      )}
                    </div>
                    <ReleaseDate entry={v} testId={`terms-history-date-${v.version}`} />
                  </div>
                  <div className="mt-1 text-[13px] text-white/65 leading-relaxed">{v.summary}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <LegalCompanyInfo className="mt-10" testid="terms-company-info" />
      </div>
    </div>
  );
}
